/**
 * PreviewCompactLayersPanel — Paths / Landers / Offers rows for the side
 * panel.
 *
 *   POOL dropdown (flows with 2+ path pools): scopes every list to one
 *   allocation pool — the paths of that pool, and the landers/offers of
 *   those paths. Ranks restart at #1 within the pool.
 *
 *   Viewing ALL pools, the paths list is split into one block per pool
 *   (default paths first, then each rule group) — items only compete inside
 *   their own pool, so a flat mixed list invited false comparisons.
 *
 *   PATHS ONLY until a path is picked. Landers and offers are the long tail
 *   of this panel (a flow carries a handful of paths but hundreds of
 *   landers), and unfiltered they answer a question nobody asked. Click a
 *   path to reveal that path's landers and offers; click it again to
 *   collapse back to paths.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, memo, useMemo, useState } from 'react';
import type {
  FlowLayer,
  FlowOptimizerItemPreview,
  FlowOptimizerPreviewResult,
} from '@trafficpulse/shared';

import PreviewLayerSection from './PreviewLayerSection';
import PreviewListRankControls from '../PreviewListRankControls';
import { applyPreviewListFilters } from '../preview-list-filters';
import {
  buildPathPoolIndex,
  filterItemsByPool,
  poolOptionsFromResult,
} from '../preview-pool-filter';
import { groupPathsByPool } from '../preview-pool-groups';
import {
  dedupePreviewItems,
  filterPreviewItemsByRank,
  maxRankForFilter,
  rankPreviewItems,
  sortPreviewItemsByMode,
  type PreviewListSortMode,
  type PreviewRankFilter,
} from '../preview-sort';
import type { PreviewOutcomeFilter, PreviewStatusFilter } from '../preview-list-filters';

const LAYERS: ReadonlyArray<{ id: FlowLayer; label: string }> = [
  { id: 'paths', label: 'Paths' },
  { id: 'landers', label: 'Landers' },
  { id: 'offers', label: 'Offers' },
];

export interface PreviewCompactLayersPanelProps {
  result: FlowOptimizerPreviewResult;
}

interface LayerBuckets {
  paths: FlowOptimizerItemPreview[];
  landers: FlowOptimizerItemPreview[];
  offers: FlowOptimizerItemPreview[];
}

const PreviewCompactLayersPanel: FC<PreviewCompactLayersPanelProps> = ({
  result,
}) => {
  const byLayer = useMemo<LayerBuckets>(() => {
    const map: LayerBuckets = { paths: [], landers: [], offers: [] };
    for (const item of result.items) map[item.layer].push(item);
    return map;
  }, [result.items]);

  const pools = useMemo(() => poolOptionsFromResult(result), [result]);
  const pathPools = useMemo(
    () => buildPathPoolIndex(byLayer.paths),
    [byLayer.paths],
  );

  const [selectedPathId, setSelectedPathId] = useState<string | null>(null);
  const [poolFilter, setPoolFilter] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<PreviewListSortMode>('rank');
  const [rankFilter, setRankFilter] = useState<PreviewRankFilter>('all');
  const [statusFilter, setStatusFilter] = useState<PreviewStatusFilter>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<PreviewOutcomeFilter>('all');

  const handleTogglePath = (id: string) =>
    setSelectedPathId((prev) => (prev === id ? null : id));

  const handlePoolChange = (pool: string | null) => {
    setPoolFilter(pool);
    // A selected path outside the new pool would silently empty the lists.
    if (
      selectedPathId != null &&
      pool != null &&
      pathPools.get(selectedPathId) !== pool
    ) {
      setSelectedPathId(null);
    }
  };

  const filterByPath = (items: FlowOptimizerItemPreview[]) => {
    if (!selectedPathId) return items;
    return items.filter((i) => (i.pathId ?? i.parentId) === selectedPathId);
  };

  const poolLabel = pools.find((p) => p.id === poolFilter)?.name;
  // Landers/offers only exist in this panel as "what does THIS path run?".
  const visibleLayers = selectedPathId ? LAYERS : LAYERS.slice(0, 1);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-0.5">
      <PreviewListRankControls
        sortMode={sortMode}
        rankFilter={rankFilter}
        statusFilter={statusFilter}
        outcomeFilter={outcomeFilter}
        onSortModeChange={setSortMode}
        onRankFilterChange={setRankFilter}
        onStatusFilterChange={setStatusFilter}
        onOutcomeFilterChange={setOutcomeFilter}
        pools={pools}
        poolFilter={poolFilter}
        onPoolFilterChange={handlePoolChange}
        selectedPathName={
          selectedPathId
            ? byLayer.paths.find((p) => p.itemId === selectedPathId)?.itemName
            : undefined
        }
        onClearPath={selectedPathId ? () => setSelectedPathId(null) : undefined}
      />
      {visibleLayers.map((layer) => {
        const pooled = filterItemsByPool(
          byLayer[layer.id],
          layer.id,
          poolFilter,
          pathPools,
        );
        const raw = layer.id === 'paths' ? pooled : filterByPath(pooled);
        const scoped = applyPreviewListFilters(raw, { status: statusFilter, outcome: outcomeFilter });
        const deduped = layer.id === 'paths' ? scoped : dedupePreviewItems(scoped);
        const ranks = rankPreviewItems(deduped);
        const filtered = filterPreviewItemsByRank(
          deduped,
          ranks,
          maxRankForFilter(rankFilter),
        );
        const items = sortPreviewItemsByMode(filtered, sortMode);
        // Group only when no single pool is already selected — a scoped list
        // is one pool by definition, so a lone group header adds nothing.
        const groups =
          layer.id === 'paths' && poolFilter == null
            ? groupPathsByPool(items, pools)
            : null;
        return (
          <PreviewLayerSection
            key={layer.id}
            label={layer.label}
            poolLabel={poolLabel}
            items={items}
            ranks={ranks}
            groups={groups}
            filteredByPath={layer.id !== 'paths' && selectedPathId != null}
            onSelectPath={layer.id === 'paths' ? handleTogglePath : undefined}
            selectedPathId={selectedPathId}
          />
        );
      })}
    </div>
  );
};

// Memoized on `result` (referentially stable between previews): a keystroke in
// the formula/exclusion editor re-renders the whole drawer, but this 100+ row
// panel must not re-run its filter/dedupe/rank/sort pipeline or repaint until
// a new preview actually arrives.
export default memo(PreviewCompactLayersPanel);
